import * as React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { WidgetConfigView } from '@/api/types';

interface WidgetPreviewProps {
  config: WidgetConfigView;
}

const POSTMESSAGE_TYPE = 'zorro:preview';
const DEBOUNCE_MS = 200;
const DEFAULT_DEV_PREVIEW_URL = 'http://localhost:5174/widget-preview.html';
const DEFAULT_PROD_BUNDLE_URL = 'https://zorro-widget.upela.com/v1/widget.js';

export function WidgetPreview({ config }: WidgetPreviewProps): JSX.Element {
  const iframeRef = React.useRef<HTMLIFrameElement | null>(null);
  const [ready, setReady] = React.useState(false);

  const isDev = import.meta.env.DEV;
  const devPreviewUrl =
    (import.meta.env.VITE_WIDGET_PREVIEW_URL as string | undefined) || DEFAULT_DEV_PREVIEW_URL;
  const bundleUrl =
    (import.meta.env.VITE_WIDGET_BUNDLE_URL as string | undefined) ||
    config.embedSnippetUrl ||
    DEFAULT_PROD_BUNDLE_URL;

  const srcDoc = React.useMemo(
    () => (isDev ? undefined : buildSrcDoc(bundleUrl, config)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isDev, bundleUrl],
  );

  React.useEffect(() => {
    const onMessage = (e: MessageEvent): void => {
      if (e.source !== iframeRef.current?.contentWindow) return;
      if (e.data?.type === 'zorro:preview:ready') setReady(true);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  React.useEffect(() => {
    if (!ready) return;
    const handle = setTimeout(() => {
      iframeRef.current?.contentWindow?.postMessage(
        { type: POSTMESSAGE_TYPE, config: toPreviewShape(config) },
        '*',
      );
    }, DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [ready, config]);

  return (
    <Card className="overflow-hidden">
      <CardHeader>
        <CardTitle className="text-base">Aperçu</CardTitle>
        <p className="mt-1 text-xs text-muted-foreground">
          Preview only
        </p>
      </CardHeader>
      <CardContent className="p-0">
        <iframe
          ref={iframeRef}
          title="Widget preview"
          {...(isDev ? { src: devPreviewUrl } : { srcDoc })}
          sandbox={isDev ? 'allow-scripts allow-same-origin' : 'allow-scripts'}
          style={{
            width: '100%',
            height: '480px',
            border: 0,
            display: 'block',
            background: '#f8fafc',
          }}
        />
      </CardContent>
    </Card>
  );
}

function toPreviewShape(config: WidgetConfigView): unknown {
  return {
    botName: 'Aperçu',
    theme: config.theme,
    launcherLabel: config.launcherLabel,
    welcomeMessage: config.welcomeMessage,
    suggestions: config.suggestions,
  };
}

function buildSrcDoc(bundleUrl: string, config: WidgetConfigView): string {
  const initial = JSON.stringify(toPreviewShape(config));
  return `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <style>
    html, body { margin: 0; padding: 0; height: 100%; background: #f8fafc; font-family: system-ui; }
    .hint { padding: 16px; color: #64748b; font-size: 12px; }
  </style>
</head>
<body>
  <div class="hint">Cliquez sur la bulle en bas pour ouvrir le chat (mode aperçu — pas d'appel réseau).</div>
  <script>
    window.__zorroPreviewConfig = ${initial};
    window.addEventListener('message', function (e) {
      if (e.data && e.data.type === 'zorro:preview' && window.ZorroChat && window.ZorroChat.applyPreviewConfig) {
        window.ZorroChat.applyPreviewConfig(e.data.config);
      }
    });
  </script>
  <script async src="${bundleUrl}" data-preview="true"></script>
  <script>
    // Some bundles load synchronously after the data attribute path — emit
    // ready once the global is available, on every animation frame until it is.
    (function waitReady() {
      if (window.ZorroChat && typeof window.ZorroChat.initPreview === 'function') {
        try { window.ZorroChat.initPreview(window.__zorroPreviewConfig); } catch (e) { console.error(e); }
        parent.postMessage({ type: 'zorro:preview:ready' }, '*');
        return;
      }
      requestAnimationFrame(waitReady);
    })();
  </script>
</body>
</html>`;
}
